import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
} from "react";
import websocketService from "../services/websocket";

// Auth Context
const AuthContext = createContext();

// Auth Actions
const AUTH_ACTIONS = {
  LOGIN_START: "LOGIN_START",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  REFRESH_TOKEN: "REFRESH_TOKEN",
  SET_LOADING: "SET_LOADING",
  SET_USER: "SET_USER",
  CLEAR_ERROR: "CLEAR_ERROR",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  SESSION_CHECK_COMPLETE: "SESSION_CHECK_COMPLETE",
};

// Initial State
const initialState = {
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  sessionChecked: false,
};

// Auth Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: {
          ...action.payload.user,
          profile: action.payload.profile,
        },
        accessToken: action.payload.accessToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        sessionChecked: true,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        sessionChecked: true,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
        sessionChecked: true,
      };

    case AUTH_ACTIONS.REFRESH_TOKEN:
      return {
        ...state,
        accessToken: action.payload.accessToken,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: state.user
          ? { ...state.user, ...action.payload }
          : action.payload,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTIONS.TOKEN_EXPIRED:
      return {
        ...initialState,
        isLoading: false,
        sessionChecked: true,
        error: "Session expired. Please log in again.",
      };

    case AUTH_ACTIONS.SESSION_CHECK_COMPLETE:
      return {
        ...state,
        sessionChecked: true,
        isLoading: false,
      };

    default:
      return state;
  }
};

const decodeToken = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const sessionCheckRef = useRef(false);
  const tokenRefreshTimerRef = useRef(null);
  const refreshPromiseRef = useRef(null);
  const tokenRef = useRef(state.accessToken);

  const isAuthenticatedRef = useRef(state.isAuthenticated);

  useEffect(() => {
    isAuthenticatedRef.current = state.isAuthenticated;
    tokenRef.current = state.accessToken;
  }, [state.isAuthenticated, state.accessToken]);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

  // 5. scheduleTokenRefresh - only depends on API_URL
  const scheduleTokenRefresh = useCallback(
    (token) => {
      if (tokenRefreshTimerRef.current) {
        clearTimeout(tokenRefreshTimerRef.current);
        tokenRefreshTimerRef.current = null;
      }

      if (!token) return;

      try {
        const decoded = decodeToken(token);
        if (!decoded || !decoded.exp) {
          return;
        }

        const now = Date.now();
        const expiry = decoded.exp * 1000;
        const timeUntilExpiry = expiry - now;

        if (timeUntilExpiry <= 0) {
          return;
        }

        const refreshTime = timeUntilExpiry * 0.75;

        tokenRefreshTimerRef.current = setTimeout(async () => {
          try {
            const response = await fetch(`${API_URL}/api/auth/refresh`, {
              method: "POST",
              credentials: "include",
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.data.accessToken) {
                dispatch({
                  type: AUTH_ACTIONS.REFRESH_TOKEN,
                  payload: { accessToken: data.data.accessToken },
                });

                scheduleTokenRefresh(data.data.accessToken);
              }
            } else {
              dispatch({ type: AUTH_ACTIONS.TOKEN_EXPIRED });
            }
          } catch (error) {}
        }, refreshTime);
      } catch (error) {}
    },
    [API_URL]
  );

  // 1. performRefresh - no dependencies
  const performRefresh = useCallback(async () => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    refreshPromiseRef.current = (async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/refresh`, {
          method: "POST",
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data.accessToken) {
            dispatch({
              type: AUTH_ACTIONS.REFRESH_TOKEN,
              payload: { accessToken: data.data.accessToken },
            });
            return data.data.accessToken;
          }
        }
        throw new Error("Refresh failed");
      } catch (error) {
        // ✅ Use the REF, not the STATE dependency
        if (isAuthenticatedRef.current) {
          dispatch({ type: AUTH_ACTIONS.TOKEN_EXPIRED });
        } else {
          dispatch({ type: AUTH_ACTIONS.SESSION_CHECK_COMPLETE });
        }
        throw error;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, [API_URL]); // 👈 REMOVE state.isAuthenticated from here!

  // 2. apiCall - depends on performRefresh
  const apiCall = useCallback(
    async (url, options = {}) => {
      const token = tokenRef.current;

      const config = {
        ...options,
        headers: {
          ...(!(options.body instanceof FormData) && {
            "Content-Type": "application/json",
          }),
          ...(token && { Authorization: `Bearer ${token}` }),
          ...options.headers,
        },
        credentials: "include",
      };

      try {
        let response = await fetch(`${API_URL}${url}`, config);

        if (response.status === 401 && token) {
          try {
            const newToken = await performRefresh();
            scheduleTokenRefresh(newToken);
            config.headers.Authorization = `Bearer ${newToken}`;
            return await fetch(`${API_URL}${url}`, config);
          } catch (err) {
            throw new Error("Session expired");
          }
        }

        return response;
      } catch (error) {
        if (error.message === "Session expired") {
          dispatch({ type: AUTH_ACTIONS.TOKEN_EXPIRED });
        }
        throw error;
      }
    },
    [API_URL, performRefresh, scheduleTokenRefresh]
  );

  // 3. checkExistingSession - depends on performRefresh
  const checkExistingSession = useCallback(async () => {
    if (sessionCheckRef.current) return;
    sessionCheckRef.current = true;

    try {
      const token = await performRefresh();

      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      if (response.ok) {
        const userData = await response.json();
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: userData.data.user,
            profile: userData.data.profile,
            accessToken: token,
          },
        });
        scheduleTokenRefresh(token);
      } else {
        dispatch({ type: AUTH_ACTIONS.SESSION_CHECK_COMPLETE });
      }
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SESSION_CHECK_COMPLETE });
    }
  }, [API_URL, performRefresh, scheduleTokenRefresh]);

  // 4. refreshUser - depends on apiCall
  const refreshUser = useCallback(async () => {
    if (!isAuthenticatedRef.current) return;

    try {
      const response = await apiCall("/api/auth/me", { method: "GET" });

      if (response.ok) {
        const userData = await response.json();
        if (userData.success) {
          dispatch({
            type: AUTH_ACTIONS.SET_USER,
            payload: {
              profile: userData.data.profile,
            },
          });
          return { success: true };
        }
      }
      return { success: false };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [apiCall]);

  // 6. login - only depends on API_URL
  const login = useCallback(
    async (email, password) => {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });

      try {
        const response = await fetch(`${API_URL}/api/auth/login`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ email, password }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user: data.data.user,
              profile: data.data.profile,
              accessToken: data.data.accessToken,
            },
          });
          scheduleTokenRefresh(data.data.accessToken);
          return { success: true, data: data.data };
        } else {
          const errorMessage = data.message || "Login failed";
          dispatch({
            type: AUTH_ACTIONS.LOGIN_FAILURE,
            payload: errorMessage,
          });
          return { success: false, error: errorMessage };
        }
      } catch (error) {
        const errorMessage = "Network error. Please check your connection.";
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: errorMessage,
        });
        return { success: false, error: errorMessage };
      }
    },
    [API_URL, scheduleTokenRefresh]
  );

  // 7. signup - only depends on API_URL
  const signup = useCallback(
    async (userData) => {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });

      try {
        const response = await fetch(`${API_URL}/api/auth/register`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: userData.email,
            password: userData.password,
            name: userData.name,
            username: userData.username,
            birthDay: userData.birthDay,
            birthMonth: userData.birthMonth,
            birthYear: userData.birthYear,
            otp: userData.otp,
          }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user: data.data.user,
              profile: data.data.profile,
              accessToken: data.data.accessToken,
            },
          });
          scheduleTokenRefresh(data.data.accessToken);
          return { success: true, data: data.data };
        } else {
          const errorMessage = data.errors
            ? data.errors.join(", ")
            : data.message || "Signup failed";

          dispatch({
            type: AUTH_ACTIONS.LOGIN_FAILURE,
            payload: errorMessage,
          });
          return { success: false, error: errorMessage, errors: data.errors };
        }
      } catch (error) {
        const errorMessage = "Network error. Please check your connection.";
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: errorMessage,
        });
        return { success: false, error: errorMessage };
      }
    },
    [API_URL, scheduleTokenRefresh]
  );

  const logout = useCallback(
    async (logoutAllDevices = false) => {
      // A. Clean up local side effects immediately
      if (tokenRefreshTimerRef.current) {
        clearTimeout(tokenRefreshTimerRef.current);
        tokenRefreshTimerRef.current = null;
      }

      websocketService.disconnect();

      try {
        const endpoint = logoutAllDevices
          ? "/api/auth/logout-all"
          : "/api/auth/logout";

        // B. Use the Ref to get the token without making this function unstable
        const token = tokenRef.current;

        // C. We don't necessarily need to 'await' this if we want a snappy UI,
        // but it's safer for 'logoutAllDevices'
        await fetch(`${API_URL}${endpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          credentials: "include",
        });
      } catch (error) {
      } finally {
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
        window.location.href = "/";
      }
    },
    [API_URL]
  );

  const deleteAccount = useCallback(async () => {
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }

    websocketService.disconnect();

    try {
      // B. Use the Ref to get the token
      const token = tokenRef.current;

      const response = await fetch(`${API_URL}/api/account/delete`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to delete account");
      }

      return { success: true, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to delete account. Please try again.",
      };
    } finally {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      window.location.href = "/";
    }
  }, [API_URL]);

  // 9. clearError - no dependencies
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  // ============ EFFECTS ============

  // Check session on mount
  useEffect(() => {
    if (!state.sessionChecked) {
      checkExistingSession();
    }
  }, [state.sessionChecked, checkExistingSession]);

  useEffect(() => {
    return () => {
      if (tokenRefreshTimerRef.current) {
        clearTimeout(tokenRefreshTimerRef.current);
        tokenRefreshTimerRef.current = null; // ADD THIS
      }
    };
  }, []);

  useEffect(() => {
    let connectionTimeout;
    let isCancelled = false;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 5;

    const connectWebSocket = async () => {
      if (isCancelled || !state.sessionChecked) return;

      if (
        state.isAuthenticated &&
        state.accessToken &&
        state.user?.profile?.id
      ) {
        try {
          if (websocketService.isConnected()) {
            await websocketService.subscribeToProfile(state.user.profile.id);
            return;
          }

          const socket = await websocketService.connect(state.accessToken);

          reconnectAttempts = 0;

          await new Promise((resolve) => setTimeout(resolve, 500));

          await websocketService.subscribeToProfile(state.user.profile.id);

          socket.on("disconnect", (reason) => {});
        } catch (error) {
          if (!isCancelled && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
            reconnectAttempts++;
            const retryDelay = Math.min(
              3000 * Math.pow(2, reconnectAttempts - 1),
              30000
            );
            connectionTimeout = setTimeout(connectWebSocket, retryDelay);
          }
        }
      } else {
        if (state.sessionChecked && !state.isAuthenticated) {
          websocketService.disconnect();
        }
      }
    };

    connectWebSocket();

    return () => {
      isCancelled = true;
      if (connectionTimeout) clearTimeout(connectionTimeout);
      // We only disconnect here if the component actually unmounts or user logs out
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.sessionChecked,
    state.isAuthenticated,
    // state.accessToken is correctly removed
    state.user?.profile?.id,
  ]);

  useEffect(() => {
    if (
      state.isAuthenticated &&
      state.accessToken &&
      websocketService.isConnected()
    ) {
      websocketService.setToken(state.accessToken);
    }
  }, [state.accessToken, state.isAuthenticated]);

  const value = {
    ...state,
    login,
    signup,
    logout,
    deleteAccount,
    clearError,
    apiCall,
    refreshUser,
    isLoading: state.isLoading || !state.sessionChecked,
    tokenRef,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const withAuth = (WrappedComponent) => {
  return (props) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      window.location.href = "/";
      return null;
    }

    return <WrappedComponent {...props} />;
  };
};